import {
  Button,
  FormControlLabel,
  makeStyles,
  Switch,
  InputAdornment,
  IconButton,
  Typography,
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Edit";
import { useContext, useMemo, useState, useEffect } from "react";
import LanguageContext from "../../contexts/LanguageContext";
import { useDefaultLanguage } from "../../hooks/useDefaultLanguage";
import EditField from "../EditField";
import LanguageSelect from "./LanguageSelect";

const useStyles = makeStyles(theme => ({
  switchLabel: {
    fontSize: "1.4rem",
    color: "var(--garnish-dark-green)",
  },
  quizSwitchLabel: {
    margin: "0 2rem",
  },
  toolbar: {
    background: "white",
    display: "flex",
    alignItems: "center",
    padding: "1rem",
    borderBottom: "1px solid var(--dark-grey)",
    height: "9rem",
    marginBottom: "3rem",
    
    "& .MuiButton-root": {
      borderRadius: "0",
      marginLeft: "2rem",
      height: "5rem",
      minWidth: "16rem",
    },
  },
  titleContainer: {
    width: "60rem",
    margin: "0 2rem",
    display: "flex",
    alignItems: "center",
    minHeight: "4.4rem", 
  },
  titleInput: {
    width: "100%",
    "& input": {
      fontSize: "2.4rem",
      fontFamily: "Montserrat",
      fontWeight: "bold",
      color: "var(--garnish-dark-green)",
      paddingRight: "0 !important"
    },
    marginBottom: "0 !important", 
  },
  readOnlyTitle: {
    fontSize: "2.4rem",
    fontFamily: "Montserrat",
    fontWeight: "bold",
    color: "var(--garnish-dark-green)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    marginRight: "0.2rem", 
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
      textDecorationColor: "var(--garnish-primary-green)"
    }
  },
  disabledTitle: {
    cursor: "default",
    opacity: 0.7,
    "&:hover": {
      textDecoration: "none"
    }
  },
  actionIcons: {
    display: "flex",
    alignItems: "center",
    marginRight: "-8px", 
  },
  iconButton: {
    padding: 0, 
    marginLeft: "0.8rem", 
    width: "2.6rem",      
    height: "2.6rem", 
    borderRadius: "4px", 
    "& .MuiSvgIcon-root": {
      width: "1.6rem", 
      height: "1.6rem", 
      fontSize: "1.6rem"
    }
  },
  editIcon: {
    opacity: 0.5,
    padding: "2px",
    marginLeft: "1rem",   // FIX: Changed from 'auto' to '1rem' to keep it next to text
    transition: "opacity 0.2s",
    width: "2.6rem",
    height: "2.6rem",
    "& .MuiSvgIcon-root": {
      width: "1.6rem", 
      height: "1.6rem", 
      fontSize: "1.6rem"
    },
    "&:hover": {
      opacity: 1,
    }
  },
  buttonWrapper: {
    display: "flex",
    marginLeft: "auto"
  },
  publishedInput: {
    marginLeft: "auto",
  },
  previewButton: {
    background: "#E2665B",
    transition: "filter 400ms ease",
    "&:hover": {
      background: "#E2665B",
      filter: "brightness(1.2)",
    },
  },
  languageSelectContainer: {
    height: "4.4rem",
    flexGrow: 1,
    marginRight: "1rem",
    minWidth: "10rem"
  },
}));

function ContentEditorToolbar({
  languageBeingEditedId,
  showQuizModeToggle,
  quizMode,
  onQuizModeChanged,
  saving,
  translatedTitles,
  title, 
  onTitleChange, 
  onTitleSave,   
  titleError,
  onLanguageBeingEditedChanged,
  published,
  onPublishedChanged,
  togglePreviewOpen,
  setTranslatedTitleForLanguageBeingEdited,
  hideSaveButton, 
  onSaveClick,    
}) {
  const classes = useStyles();
  const { defaultLanguage, defaultLanguageLoaded } = useDefaultLanguage();
  const { languageInfo } = useContext(LanguageContext);

  const [isEditing, setIsEditing] = useState(false);
  const [localTitle, setLocalTitle] = useState(title);

  useEffect(() => {
    if (!isEditing) {
      setLocalTitle(title);
    }
  }, [title, isEditing]);

  const editTranslationMode = useMemo(
    () => defaultLanguageLoaded && languageBeingEditedId !== defaultLanguage.id,
    [defaultLanguage, defaultLanguageLoaded, languageBeingEditedId]
  );

  const handleStartEdit = () => {
    if (editTranslationMode) return;
    setLocalTitle(title || "");
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setLocalTitle(title);
    setIsEditing(false);
  };

  const handleSaveEdit = () => {
    onTitleChange(localTitle);
    if (onTitleSave) {
      onTitleSave(localTitle); 
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const getQuizModeToggle = () =>
    showQuizModeToggle ? (
      <FormControlLabel
        classes={{
          root: `${classes.quizSwitchLabel} ${classes.switchLabel}`,
        }}
        labelPlacement="start"
        control={
          <Switch
            checked={quizMode}
            onChange={onQuizModeChanged}
            name="published"
            color="primary"
            disabled={saving}
          />
        }
        label="Quiz"
      />
    ) : null;

  return (
    <div className={classes.toolbar}>
      
      <div className={classes.titleContainer}>
        {isEditing ? (
          <EditField
            id="content-editor-title"
            className={classes.titleInput}
            value={localTitle}
            onChange={(e) => setLocalTitle(e.target.value)}
            error={titleError !== null}
            helperText={titleError}
            disabled={saving}
            autofocus
            inputProps={{
              onKeyDown: handleKeyDown,
              endAdornment: (
                <InputAdornment position="end">
                  <div className={classes.actionIcons}>
                    <IconButton 
                      onClick={handleSaveEdit} 
                      className={classes.iconButton}
                      disabled={saving} 
                      style={{ color: 'var(--garnish-primary-green)' }}
                      title="Save Title"
                    >
                      <SaveIcon /> 
                    </IconButton>
                    <IconButton 
                      onClick={handleCancelEdit} 
                      className={classes.iconButton}
                      color="secondary"
                      title="Cancel"
                    >
                      <CloseIcon />
                    </IconButton>
                  </div>
                </InputAdornment>
              )
            }}
          />
        ) : (
          <>
            <Typography 
              className={`${classes.readOnlyTitle} ${editTranslationMode ? classes.disabledTitle : ''}`}
              onClick={handleStartEdit}
              title={editTranslationMode ? "Edit translated title below" : "Click to edit title"}
            >
              {title || <span style={{ opacity: 0.5 }}>Untitled Content</span>}
            </Typography>
            
            {!editTranslationMode && (
              <IconButton 
                onClick={handleStartEdit} 
                className={classes.editIcon}
                size="small"
              >
                <EditIcon />
              </IconButton>
            )}
          </>
        )}
      </div>

      <div className={classes.languageSelectContainer}>
        <LanguageSelect
          id="content-editor-language-select"
          value={languageBeingEditedId}
          onChange={onLanguageBeingEditedChanged}
          maxHeight={true}
          width="100%"
        />
      </div>
      
      {getQuizModeToggle()}
      
      <FormControlLabel
        classes={{
          root: classes.publishedInput,
          label: classes.switchLabel,
        }}
        labelPlacement="start"
        control={
          <Switch
            checked={published}
            onChange={onPublishedChanged}
            name="published"
            color="primary"
            disabled={saving}
          />
        }
        label="Published"
      />
      
      <div className={classes.buttonWrapper}>
        <Button
          disableElevation
          variant="contained"
          className={classes.previewButton}
          onClick={togglePreviewOpen}
          disabled={saving}
        >
          Preview
        </Button>
        
        {!hideSaveButton && (
           <Button
             disableElevation
             variant="contained"
             color="primary"
             onClick={onSaveClick}
             disabled={saving}
           >
             Save content
           </Button>
        )}
      </div>
    </div>
  );
}

export default ContentEditorToolbar;